import I18n from "i18n-js";
import React, { Component } from "react";
import { bool, func, shape } from "prop-types";
import { connect } from "react-redux";
import TextInput from "../../common/components/form/TextInput";
import Select from "../../common/components/form/Select";
import SubmitButton from "../../common/components/form/SubmitButton";
import { getThermometersList } from "../actions/thermometersActions";
import ClientTypes from "../../company/models/ClientTypes";
import { alphabetically } from "../../common/utils/filterUtils";
import DateInput from "../../common/components/form/DateInput";
import ClientUtils from "../../company/utils/ClientUtils";

class CreateThermometerForm extends Component {
  static propTypes = {
    getThermometersList: func.isRequired,
    clients: shape().isRequired,
    thermometer: shape().isRequired,
    thermometers: shape().isRequired,
    infos: shape(),
    loading: bool,
    onSubmit: func.isRequired,
    goBack: func.isRequired,
  };

  static defaultProps = {
    infos: {},
    loading: false,
  };

  state = {
    clientError: null,
    serialNumberError: null,
    lastInspectionDateError: null,
    markError: null,
    barcodeError: null,
  };

  constructor(props) {
    super(props);

    this.updateMod = !!props.infos.uuid;
    this.lastInspectionDate =
      props.infos.lastInspectionDate &&
      new Date(props.infos.lastInspectionDate);

    this.isThermometerUnique = this.isThermometerUnique.bind(this);
    this.onChangeClient = this.onChangeClient.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    // Fields
    this.setSerialNumber = this.setSerialNumber.bind(this);
    this.setLastInspectionDate = this.setLastInspectionDate.bind(this);
    this.setDesignation = this.setDesignation.bind(this);
    this.setMark = this.setMark.bind(this);
    this.setBarcode = this.setBarcode.bind(this);
    this.setClient = this.setClient.bind(this);

    this.renderClientOption = this.renderClientOption.bind(this);
    this.renderClientsSelect = this.renderClientsSelect.bind(this);
  }

  componentDidMount() {
    this.props.getThermometersList();
  }

  /**
   * {@inheritdoc}
   */
  componentDidUpdate(prevProps) {
    const { thermometer, goBack } = this.props;

    if (
      prevProps.thermometer.saved === thermometer.saved &&
      prevProps.thermometer.errors === thermometer.errors
    ) {
      return;
    }

    if (thermometer.errors) {
      thermometer.errors.map((error) =>
        this.setState({ [`${error.path}Error`]: error.reason })
      );
    }

    if (thermometer.saved) {
      goBack();
    }
  }

  /**
   * onChange client select
   *
   * @param {Object} event
   */
  onChangeClient(event) {
    const uuid = event.target.value;
    const { clients } = this.props;
    const selectedClient = clients.list.filter(
      (client) => client.uuid === uuid
    )[0];

    if (!selectedClient) {
      this.setState({ isCompanyClient: false });

      return;
    }

    this.setState({
      isCompanyClient: selectedClient.type === ClientTypes.COMPANY,
    });
  }

  onSubmit() {
    if (!this.isValid()) {
      return;
    }

    const newThermometer = {
      serialNumber: this.serialNumber.value,
      lastInspectionDate: this.lastInspectionDate.toISOString(),
      designation: this.designation.value || null,
      mark: this.mark.value || null,
      barcode: this.barcode.value || null,
    };

    if (!this.updateMod) {
      newThermometer.clientUuid = this.client.value;
    }

    const { thermometer } = this.props;

    if (
      !this.isThermometerUnique(
        newThermometer,
        thermometer.infos ? thermometer.infos.uuid : null
      )
    ) {
      return;
    }

    this.props.onSubmit(newThermometer);
  }

  /**
   * @param {String} serialNumber
   */
  setSerialNumber(serialNumber) {
    this.serialNumber = serialNumber;
  }

  /**
   * @param {Date} lastInspectionDate
   */
  setLastInspectionDate(lastInspectionDate) {
    this.lastInspectionDate = lastInspectionDate;
  }

  /**
   * @param {String} designation
   */
  setDesignation(designation) {
    this.designation = designation;
  }

  /**
   * @param {String} mark
   */
  setMark(mark) {
    this.mark = mark;
  }

  /**
   * @param {String} barcode
   */
  setBarcode(barcode) {
    this.barcode = barcode;
  }

  /**
   * @param {String} client
   */
  setClient(client) {
    this.client = client;
  }

  /**
   * Validation thermometer unicity
   *
   * @param {Object} thermometer
   * @param {String} uuid
   *
   * @return {Boolean}
   */
  isThermometerUnique(thermometer, uuid = null) {
    const { thermometers } = this.props;
    const clientUuid = this.client.value;
    const others = thermometers.list.filter(
      (thermometer) =>
        thermometer.clientUuid === clientUuid && thermometer.uuid !== uuid
    );

    let alreadyExists = false;

    if (
      thermometer.mark &&
      others.some((thermometer) => thermometer.mark === thermometer.mark)
    ) {
      alreadyExists = true;

      this.setState({
        markError: I18n.t("pages.thermometers.add.form.mark:error:unicity"),
      });
    } else {
      this.setState({ markError: null });
    }

    if (
      thermometer.barcode &&
      others.some((thermometer) => thermometer.barcode === thermometer.barcode)
    ) {
      alreadyExists = true;

      this.setState({
        barcodeError: I18n.t(
          "pages.thermometers.add.form.barcode:error:unicity"
        ),
      });
    } else {
      this.setState({ barcodeError: null });
    }

    return !alreadyExists;
  }

  /**
   * @return {Boolean}
   */
  isValid() {
    const serialNumberLength = this.serialNumber.value.length;
    const clientLength = this.client.value.length;

    const serialNumberErrorText = I18n.t(
      "pages.thermometers.add.form.serial_number:error:empty"
    );
    const lastInspectionDateErrorText = I18n.t(
      "pages.thermometers.add.form.last_inspection_date:error:empty"
    );
    const clientErrorText = I18n.t(
      "pages.thermometers.add.form.client:error:empty"
    );

    this.setState({
      serialNumberError: !serialNumberLength ? serialNumberErrorText : null,
      lastInspectionDateError: !this.lastInspectionDate
        ? lastInspectionDateErrorText
        : null,
      clientError: !clientLength ? clientErrorText : null,
    });

    return serialNumberLength && this.lastInspectionDate && clientLength;
  }

  /**
   * @param {Object} option
   *
   * @return {Element}
   */
  renderClientOption(option) {
    return (
      <option key={`option-${option.uuid}`} value={option.uuid}>
        {ClientUtils.renderLabel(option)}
      </option>
    );
  }

  /**
   * Render clients select
   *
   * @return {Element}
   */
  renderClientsSelect() {
    const { clients, infos } = this.props;
    const { clientError } = this.state;
    let options = null;

    if (clients.list.length) {
      options = clients.list
        .filter((client) => new ClientTypes(client.type).canHaveTools())
        .sort((a, b) =>
          alphabetically(ClientUtils.renderLabel(a), ClientUtils.renderLabel(b))
        );
    }

    return (
      <Select
        id="client"
        label={I18n.t("pages.thermometers.add.form.client:label")}
        ref={this.setClient}
        options={options}
        renderOption={this.renderClientOption}
        disabled={this.updateMod}
        defaultValue={(this.updateMod && infos && infos.clientUuid) || ""}
        error={clientError}
        onChange={this.onChangeClient}
      />
    );
  }

  /**
   * {@inheritdoc}
   */
  render() {
    const { loading, infos } = this.props;
    const {
      serialNumberError,
      lastInspectionDateError,
      markError,
      barcodeError,
    } = this.state;

    return (
      <form>
        {this.renderClientsSelect()}
        <TextInput
          id="serialNumber"
          label={`${I18n.t(
            "pages.thermometers.add.form.serial_number:label"
          )}`}
          defaultValue={infos.serialNumber}
          ref={this.setSerialNumber}
          error={serialNumberError}
        />
        <DateInput
          id="lastInspectionDate"
          label={`${I18n.t(
            "pages.thermometers.add.form.last_inspection_date:label"
          )}`}
          defaultValue={this.lastInspectionDate}
          onChange={this.setLastInspectionDate}
          error={lastInspectionDateError}
        />
        <TextInput
          id="designation"
          label={I18n.t("pages.thermometers.add.form.designation:label")}
          defaultValue={infos.designation}
          ref={this.setDesignation}
          optional
        />
        <TextInput
          id="mark"
          label={I18n.t("pages.thermometers.add.form.mark:label")}
          defaultValue={infos.mark}
          ref={this.setMark}
          optional
          error={markError}
        />
        <TextInput
          id="barcode"
          label={I18n.t("pages.thermometers.add.form.barcode:label")}
          defaultValue={infos.barcode}
          ref={this.setBarcode}
          optional
          error={barcodeError}
        />
        <SubmitButton loading={loading} onClick={this.onSubmit} />
      </form>
    );
  }
}

const mapStateToProps = (state) => ({
  clients: state.clients,
  thermometer: state.thermometer,
  thermometers: state.thermometers,
});

const mapDispatchToProps = (dispatch) => ({
  getThermometersList: () => dispatch(getThermometersList()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateThermometerForm);
